import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import {db} from "../../../firebase";
import {
  buildTemplateSnapshot,
  ensureMailApnaArtistComposer,
  ensureMailApnaArtistLead,
  ensureMailApnaArtistTemplate,
  MAIL_APNAARTIST_COLLECTIONS,
  parseRecipientInput,
} from "../../types/mailApnaArtist";

function mapSnapshot(snapshot) {
  return snapshot.docs.map((item) => ({id: item.id, ...item.data()}));
}

function mapAttachmentSnapshot(attachment = {}) {
  return {
    id: String(attachment.id || "").trim(),
    fileName: String(attachment.fileName || "").trim(),
    size: Number(attachment.size || 0),
    contentType: String(attachment.contentType || "").trim(),
    downloadUrl: String(attachment.downloadUrl || "").trim(),
    storagePath: String(attachment.storagePath || "").trim(),
    status: String(attachment.status || "ready").trim(),
    storageBucket: String(attachment.storageBucket || "").trim(),
  };
}

function buildDraftPayload({composer, template, employee}) {
  const safeComposer = ensureMailApnaArtistComposer(composer);
  const safeTemplate = ensureMailApnaArtistTemplate(template);
  const to = parseRecipientInput(safeComposer.toText);
  const cc = parseRecipientInput(safeComposer.ccText);
  const bcc = parseRecipientInput(safeComposer.bccText);
  const attachments = (safeComposer.attachments || []).map(mapAttachmentSnapshot);
  const scheduledSendAtRaw = String(safeComposer.scheduledSendAt || "").trim();
  const scheduledSendDate = scheduledSendAtRaw ? new Date(scheduledSendAtRaw) : null;
  const hasValidScheduledDate = scheduledSendDate instanceof Date && !Number.isNaN(scheduledSendDate.getTime());
  const normalizedSendMode = String(safeComposer.sendMode || "instant").trim().toLowerCase() === "scheduled" ? "scheduled" : "instant";

  return {
    status: String(safeComposer.status || "draft"),
    statusNote: String(safeComposer.statusNote || "").trim(),
    sendMode: normalizedSendMode,
    scheduledSendAt: normalizedSendMode === "scheduled" && hasValidScheduledDate ? scheduledSendDate : null,
    scheduledTimezone: normalizedSendMode === "scheduled" ? String(safeComposer.scheduledTimezone || "").trim() : "",
    subject: String(safeComposer.subject || "").trim(),
    body: String(safeComposer.body || "").trim(),
    ctaLabel: String(safeComposer.ctaLabel || "").trim(),
    ctaUrl: String(safeComposer.ctaUrl || "").trim(),
    sender: {
      accountId: String(safeComposer.senderAccountId || "").trim(),
      connectedEmail: String(safeComposer.senderConnectedEmail || "").trim(),
      fromEmail: String(safeComposer.senderEmail || "").trim(),
      fromDisplayName: String(
          safeComposer.senderDisplayName ||
          employee?.fullName ||
          employee?.name ||
          "ApnaArtist Admin",
      ).trim(),
      aliasSource: String(safeComposer.senderAliasSource || "").trim(),
      sendReady: safeComposer.senderSendReady === true,
      replyToAddress: String(safeComposer.senderReplyToAddress || "").trim(),
    },
    recipients: to,
    to,
    cc,
    bcc,
    templateId: String(safeTemplate.id || safeComposer.templateId || "").trim(),
    templateSnapshot: buildTemplateSnapshot({
      ...safeTemplate,
      bannerImageUrl: safeComposer.bannerImageUrl || safeTemplate.bannerImageUrl,
      bannerAlt: safeComposer.bannerAlt || safeTemplate.bannerAlt,
    }),
    attachmentNames: attachments.map((item) => item.fileName).filter(Boolean),
    attachments,
    attachmentCount: attachments.length,
    replyTrackingState: String(safeComposer.replyTrackingState || "reply_pending"),
    failedReason: String(safeComposer.failedReason || "").trim(),
    createdBy: {
      docId: String(employee?.docId || ""),
      role: String(employee?.role || ""),
      name: String(employee?.fullName || employee?.name || ""),
      email: String(employee?.email || ""),
    },
    updatedAt: serverTimestamp(),
  };
}

export function subscribeMailApnaArtistStudio({onData, onError}) {
  const state = {
    settings: [],
    senders: [],
    gmailAccounts: [],
    contacts: [],
    templates: [],
    messages: [],
    events: [],
    attachments: [],
  };

  const publish = () => onData?.({...state});

  const unsubscribers = [
    onSnapshot(collection(db, MAIL_APNAARTIST_COLLECTIONS.settings), (snapshot) => {
      state.settings = mapSnapshot(snapshot);
      publish();
    }, onError),
    onSnapshot(collection(db, MAIL_APNAARTIST_COLLECTIONS.senders), (snapshot) => {
      state.senders = mapSnapshot(snapshot);
      publish();
    }, onError),
    onSnapshot(collection(db, MAIL_APNAARTIST_COLLECTIONS.gmailAccounts), (snapshot) => {
      state.gmailAccounts = mapSnapshot(snapshot);
      publish();
    }, onError),
    onSnapshot(collection(db, MAIL_APNAARTIST_COLLECTIONS.contacts), (snapshot) => {
      state.contacts = mapSnapshot(snapshot);
      publish();
    }, onError),
    onSnapshot(collection(db, MAIL_APNAARTIST_COLLECTIONS.templates), (snapshot) => {
      state.templates = mapSnapshot(snapshot);
      publish();
    }, onError),
    onSnapshot(collection(db, MAIL_APNAARTIST_COLLECTIONS.messages), (snapshot) => {
      state.messages = mapSnapshot(snapshot);
      publish();
    }, onError),
    onSnapshot(collection(db, MAIL_APNAARTIST_COLLECTIONS.events), (snapshot) => {
      state.events = mapSnapshot(snapshot);
      publish();
    }, onError),
    onSnapshot(collection(db, MAIL_APNAARTIST_COLLECTIONS.attachments), (snapshot) => {
      state.attachments = mapSnapshot(snapshot);
      publish();
    }, onError),
  ];

  publish();
  return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
}

export async function saveMailApnaArtistDraft({composer, template, employee}) {
  const payload = buildDraftPayload({composer, template, employee});
  const messageId = String(composer?.messageId || "").trim();

  if (messageId) {
    const ref = doc(db, MAIL_APNAARTIST_COLLECTIONS.messages, messageId);
    await setDoc(ref, payload, {merge: true});
    return ref;
  }

  const ref = doc(collection(db, MAIL_APNAARTIST_COLLECTIONS.messages));
  await setDoc(ref, {
    ...payload,
    createdAt: serverTimestamp(),
  });
  return ref;
}

export async function upsertMailApnaArtistAttachmentMeta(attachmentInput = {}, employee = {}) {
  const attachment = mapAttachmentSnapshot(attachmentInput);
  const ref = attachment.id ?
    doc(db, MAIL_APNAARTIST_COLLECTIONS.attachments, attachment.id) :
    doc(collection(db, MAIL_APNAARTIST_COLLECTIONS.attachments));

  await setDoc(ref, {
    ...attachment,
    id: ref.id,
    uploadedBy: {
      docId: String(employee?.docId || ""),
      role: String(employee?.role || ""),
      name: String(employee?.fullName || employee?.name || ""),
      email: String(employee?.email || ""),
    },
    updatedAt: serverTimestamp(),
    createdAt: attachment.id ? (attachmentInput.createdAt || serverTimestamp()) : serverTimestamp(),
  }, {merge: true});

  return ref;
}

export async function upsertMailApnaArtistLead(leadInput = {}, employee = {}) {
  const lead = ensureMailApnaArtistLead(leadInput);
  const ref = lead.id ?
    doc(db, MAIL_APNAARTIST_COLLECTIONS.contacts, lead.id) :
    doc(collection(db, MAIL_APNAARTIST_COLLECTIONS.contacts));

  await setDoc(ref, {
    id: ref.id,
    clientName: String(lead.clientName || "").trim(),
    name: String(lead.clientName || "").trim(),
    fullName: String(lead.clientName || "").trim(),
    email: String(lead.email || "").trim().toLowerCase(),
    phone: String(lead.phone || "").trim(),
    location: String(lead.location || "").trim(),
    updatedAt: serverTimestamp(),
    updatedBy: {
      docId: String(employee?.docId || ""),
      role: String(employee?.role || ""),
      name: String(employee?.fullName || employee?.name || ""),
      email: String(employee?.email || ""),
    },
    createdAt: lead.id ? (leadInput.createdAt || serverTimestamp()) : serverTimestamp(),
  }, {merge: true});

  return ref;
}

export async function deleteMailApnaArtistLead(leadId = "") {
  const normalizedLeadId = String(leadId || "").trim();
  if (!normalizedLeadId) return;
  await deleteDoc(doc(db, MAIL_APNAARTIST_COLLECTIONS.contacts, normalizedLeadId));
}

export async function upsertMailApnaArtistTemplate(templateInput = {}, employee = {}) {
  const template = ensureMailApnaArtistTemplate(templateInput);
  const payload = {
    ...buildTemplateSnapshot(template),
    templateName: String(template.templateName || "").trim(),
    updatedAt: serverTimestamp(),
    updatedBy: {
      docId: String(employee?.docId || ""),
      role: String(employee?.role || ""),
      name: String(employee?.fullName || employee?.name || ""),
      email: String(employee?.email || ""),
    },
  };

  if (template.id) {
    const ref = doc(db, MAIL_APNAARTIST_COLLECTIONS.templates, template.id);
    await setDoc(ref, payload, {merge: true});
    return ref;
  }

  const ref = doc(collection(db, MAIL_APNAARTIST_COLLECTIONS.templates));
  await setDoc(ref, {
    ...payload,
    createdAt: serverTimestamp(),
  });
  return ref;
}

export async function sendMailApnaArtistMessage(payload = {}) {
  const response = await fetch("/api/mail-apnaartist/google/send", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload || {}),
  });

  let data = {};
  try {
    data = await response.json();
  } catch (error) {
    data = {};
  }

  if (!response.ok || data?.ok === false) {
    throw new Error(String(data?.message || data?.error || "Mail ApnaArtist send failed."));
  }

  return data;
}
